'use client';

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import JSZip from 'jszip';
import type { ToolEntry } from '@/lib/tools';

type PresetId = 'amazon-listing' | 'shopify-product' | 'etsy-listing' | 'custom';
type UniquenessMode = 'index' | 'original-suffix';

type UploadItem = {
  id: string;
  file: File;
  originalName: string;
  originalBase: string;
  sanitizedOriginalBase: string;
  extension: string;
};

type PreviewRow = {
  id: string;
  originalName: string;
  extension: string;
  autoBase: string;
  finalBase: string;
  finalName: string;
  edited: boolean;
};

type PresetConfig = {
  id: PresetId;
  label: string;
  hint: string;
  order: Array<'brand' | 'product' | 'attributes' | 'year' | 'suffix'>;
  uniquenessMode: UniquenessMode;
};

const MAX_FILES = 200;
const MAX_BASE_LENGTH = 72;

const namingPresets: PresetConfig[] = [
  {
    id: 'amazon-listing',
    label: 'Amazon listing',
    hint: 'brand → product → attributes → year',
    order: ['brand', 'product', 'attributes', 'year'],
    uniquenessMode: 'index',
  },
  {
    id: 'shopify-product',
    label: 'Shopify product',
    hint: 'brand → product → attributes → suffix',
    order: ['brand', 'product', 'attributes', 'suffix'],
    uniquenessMode: 'original-suffix',
  },
  {
    id: 'etsy-listing',
    label: 'Etsy listing',
    hint: 'product → attributes → brand → year',
    order: ['product', 'attributes', 'brand', 'year'],
    uniquenessMode: 'original-suffix',
  },
  {
    id: 'custom',
    label: 'Custom',
    hint: 'choose your own naming inputs',
    order: ['brand', 'product', 'attributes', 'year', 'suffix'],
    uniquenessMode: 'index',
  },
];

const extensionByMimeType: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/bmp': 'bmp',
  'image/tiff': 'tif',
  'image/avif': 'avif',
};

function getPresetConfig(presetId: PresetId) {
  return namingPresets.find((preset) => preset.id === presetId) ?? namingPresets[0];
}

function stripExtension(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function getFileExtension(file: File) {
  const lastDot = file.name.lastIndexOf('.');
  if (lastDot > 0) {
    return file.name.slice(lastDot + 1).toLowerCase();
  }

  return extensionByMimeType[file.type] ?? 'jpg';
}

function slugifySegment(value: string) {
  const ascii = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x00-\x7F]/g, '');

  return ascii
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function splitAttributes(value: string) {
  return value
    .split(',')
    .map((token) => slugifySegment(token))
    .filter(Boolean);
}

function safeTruncateSlug(value: string, maxLength: number) {
  if (value.length <= maxLength) {
    return value;
  }

  const trimmed = value.slice(0, maxLength).replace(/-+$/g, '');
  const lastDash = trimmed.lastIndexOf('-');

  if (lastDash > Math.max(12, Math.floor(maxLength * 0.55))) {
    return trimmed.slice(0, lastDash);
  }

  return trimmed;
}

function extractOriginalSuffix(item: UploadItem) {
  const parts = item.sanitizedOriginalBase.split('-').filter(Boolean);

  if (parts.length > 1) {
    return parts[parts.length - 1];
  }

  return safeTruncateSlug(item.sanitizedOriginalBase || item.originalBase || 'image', 18);
}

function buildBaseSegments(
  preset: PresetConfig,
  values: {
    brand: string;
    productName: string;
    attributes: string[];
    year: string;
    customSuffix: string;
  },
) {
  const parts: string[] = [];

  for (const token of preset.order) {
    if (token === 'attributes') {
      parts.push(...values.attributes);
      continue;
    }

    if (token === 'brand' && values.brand) {
      parts.push(values.brand);
      continue;
    }

    if (token === 'product' && values.productName) {
      parts.push(values.productName);
      continue;
    }

    if (token === 'year' && values.year) {
      parts.push(values.year);
      continue;
    }

    if (token === 'suffix' && values.customSuffix) {
      parts.push(values.customSuffix);
    }
  }

  return parts.filter(Boolean);
}

function buildDownloadName(fileName: string, content: Blob) {
  const blob = new Blob([content], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type BrowserFileNameSeoGeneratorProps = {
  entry?: ToolEntry;
};

export default function BrowserFileNameSeoGenerator({ entry }: BrowserFileNameSeoGeneratorProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const productNameRef = useRef<HTMLInputElement | null>(null);

  const [items, setItems] = useState<UploadItem[]>([]);
  const [presetId, setPresetId] = useState<PresetId>('amazon-listing');
  const [brand, setBrand] = useState('');
  const [productName, setProductName] = useState('');
  const [attributesInput, setAttributesInput] = useState('');
  const [year, setYear] = useState('');
  const [customSuffix, setCustomSuffix] = useState('');
  const [uniquenessMode, setUniquenessMode] = useState<UniquenessMode>('index');
  const [showOptional, setShowOptional] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionTone, setActionTone] = useState<'neutral' | 'success' | 'warning'>('neutral');
  const [isPreparingZip, setIsPreparingZip] = useState(false);

  const preset = getPresetConfig(presetId);
  const brandSlug = slugifySegment(brand);
  const productSlug = slugifySegment(productName);
  const attributes = splitAttributes(attributesInput);
  const yearSlug = slugifySegment(year)
    .replace(/[^0-9]+/g, '')
    .slice(0, 4);
  const customSuffixSlug = slugifySegment(customSuffix);

  const previewRows = useMemo<PreviewRow[]>(() => {
    const generatedBaseParts = buildBaseSegments(preset, {
      brand: brandSlug,
      productName: productSlug,
      attributes,
      year: yearSlug,
      customSuffix: customSuffixSlug,
    });
    const generatedBase = safeTruncateSlug(generatedBaseParts.join('-'), MAX_BASE_LENGTH);
    const usedNames = new Map<string, number>();

    return items.map((item, index) => {
      let autoBase = generatedBase;

      if (!productSlug) {
        autoBase = safeTruncateSlug(
          item.sanitizedOriginalBase || item.originalBase || `image-${index + 1}`,
          MAX_BASE_LENGTH,
        );
      } else if (items.length > 1) {
        if (uniquenessMode === 'index') {
          autoBase = safeTruncateSlug(`${generatedBase}-${index + 1}`, MAX_BASE_LENGTH);
        } else {
          autoBase = safeTruncateSlug(
            `${generatedBase}-${extractOriginalSuffix(item)}`,
            MAX_BASE_LENGTH,
          );
        }
      }

      const overrideBase = slugifySegment(overrides[item.id] ?? '');
      let finalBase = safeTruncateSlug(
        overrideBase || autoBase || item.sanitizedOriginalBase || `image-${index + 1}`,
        MAX_BASE_LENGTH,
      );
      const seen = usedNames.get(finalBase) ?? 0;

      if (seen > 0) {
        const suffix = `-${seen + 1}`;
        finalBase = `${safeTruncateSlug(finalBase, Math.max(12, MAX_BASE_LENGTH - suffix.length))}${suffix}`;
      }

      usedNames.set(finalBase, seen + 1);

      return {
        id: item.id,
        originalName: item.originalName,
        extension: item.extension,
        autoBase,
        finalBase,
        finalName: `${finalBase}.${item.extension}`,
        edited: Boolean(overrideBase),
      };
    });
  }, [
    attributes,
    brandSlug,
    customSuffixSlug,
    items,
    overrides,
    preset,
    productSlug,
    uniquenessMode,
    yearSlug,
  ]);

  const sampleOutput =
    previewRows[0]?.finalName ??
    `${safeTruncateSlug(
      buildBaseSegments(preset, {
        brand: brandSlug,
        productName: productSlug || 'product-name',
        attributes,
        year: yearSlug,
        customSuffix: customSuffixSlug,
      }).join('-') || 'product-name',
      MAX_BASE_LENGTH,
    )}.jpg`;

  useEffect(() => {
    if (items.length && !productName.trim()) {
      productNameRef.current?.focus();
      productNameRef.current?.select();
    }
  }, [items.length, productName]);

  function setPresetAndDefaults(nextPresetId: PresetId) {
    const selected = getPresetConfig(nextPresetId);
    setPresetId(nextPresetId);
    setUniquenessMode(selected.uniquenessMode);
    setActionTone('neutral');
    setActionMessage(`${selected.label} pattern applied.`);
  }

  function appendFiles(fileList: FileList | File[]) {
    const source = Array.from(fileList);
    const imageFiles = source.filter((file) => file.type.startsWith('image/'));
    const nextItems = imageFiles.slice(0, Math.max(0, MAX_FILES - items.length)).map((file) => ({
      id: crypto.randomUUID(),
      file,
      originalName: file.name,
      originalBase: stripExtension(file.name),
      sanitizedOriginalBase: slugifySegment(stripExtension(file.name)),
      extension: getFileExtension(file),
    }));

    setItems((current) => [...current, ...nextItems]);
    setDragActive(false);

    if (!imageFiles.length) {
      setActionTone('warning');
      setActionMessage('Only image files can be renamed here.');
      return;
    }

    if (!nextItems.length) {
      setActionTone('warning');
      setActionMessage(`Up to ${MAX_FILES} files can stay in the batch at once.`);
      return;
    }

    setActionTone('neutral');
    setActionMessage(
      nextItems.length === 1
        ? '1 image added to the rename batch.'
        : `${nextItems.length} images added to the rename batch.`,
    );
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files?.length) {
      appendFiles(event.target.files);
      event.target.value = '';
    }
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragActive(false);

    if (event.dataTransfer.files?.length) {
      appendFiles(event.dataTransfer.files);
    }
  }

  function clearAll() {
    setItems([]);
    setOverrides({});
    setEditingId(null);
    setActionTone('neutral');
    setActionMessage('Batch cleared.');
  }

  function removeItem(itemId: string) {
    setItems((current) => current.filter((item) => item.id !== itemId));
    setOverrides((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
    setEditingId((current) => (current === itemId ? null : current));
    setActionTone('neutral');
    setActionMessage('File removed from the batch.');
  }

  function updateOverride(itemId: string, value: string) {
    setOverrides((current) => ({
      ...current,
      [itemId]: value,
    }));
  }

  function commitOverride(itemId: string) {
    const nextValue = slugifySegment(overrides[itemId] ?? '');
    setOverrides((current) => {
      if (!nextValue) {
        const next = { ...current };
        delete next[itemId];
        return next;
      }

      return {
        ...current,
        [itemId]: nextValue,
      };
    });
    setEditingId(null);
  }

  async function copyNames() {
    if (!previewRows.length) {
      setActionTone('warning');
      setActionMessage('Add at least one image to copy filenames.');
      return;
    }

    try {
      await copyToClipboard(previewRows.map((row) => row.finalName).join('\n'));
      setActionTone('success');
      setActionMessage('New filenames copied.');
    } catch {
      setActionTone('warning');
      setActionMessage(
        'Copy failed in this browser. Select the preview table and copy the names manually.',
      );
    }
  }

  async function copyMapping() {
    if (!previewRows.length) {
      setActionTone('warning');
      setActionMessage('Add at least one image to copy the rename map.');
      return;
    }

    try {
      await copyToClipboard(
        previewRows.map((row) => `${row.originalName} -> ${row.finalName}`).join('\n'),
      );
      setActionTone('success');
      setActionMessage('Original to new filename mapping copied.');
    } catch {
      setActionTone('warning');
      setActionMessage(
        'Copy failed in this browser. Try the ZIP export or copy the table manually.',
      );
    }
  }

  async function downloadZip() {
    if (!previewRows.length) {
      setActionTone('warning');
      setActionMessage('Add images before downloading a ZIP pack.');
      return;
    }

    setIsPreparingZip(true);

    try {
      const zip = new JSZip();

      await Promise.all(
        previewRows.map(async (row) => {
          const item = items.find((entry) => entry.id === row.id);
          if (!item) {
            return;
          }

          zip.file(row.finalName, await item.file.arrayBuffer());
        }),
      );

      const blob = await zip.generateAsync({ type: 'blob' });
      buildDownloadName('seo-file-names.zip', blob);
      setActionTone('success');
      setActionMessage('ZIP pack downloaded with renamed files.');
    } catch {
      setActionTone('warning');
      setActionMessage('ZIP export failed in this browser. Try again with a smaller batch.');
    } finally {
      setIsPreparingZip(false);
    }
  }

  const filledCoreFields = [brandSlug, productSlug].filter(Boolean).length;
  const visibleTitle = entry?.title ?? 'Rename product images for search and catalog uploads';
  const visibleSubtitle =
    entry?.intro ??
    'Drop in product images, set one naming pattern, preview the new filenames, and download a ZIP pack without uploading files anywhere.';
  const headerLabel =
    entry?.heroEyebrow && entry.heroEyebrow !== 'Batch rename'
      ? `${entry.heroEyebrow} workflow`
      : 'Browser-only batch rename';
  const benefits = entry?.highlights.slice(0, 3) ?? [
    'Rename product images in batch with SEO-friendly naming.',
    'Preview the final names before export.',
    'Download the renamed files locally as a ZIP pack.',
  ];

  return (
    <section className="tool-live-shell tool-file-seo-shell">
      <div className="article-meta-strip">
        <span>{headerLabel}</span>
        <span>Local ZIP export</span>
      </div>
      <h1 className="tool-live-title">{visibleTitle}</h1>
      <p className="tool-live-subtitle">{visibleSubtitle}</p>
      <div className="tool-file-seo-benefits" aria-label="Benefits">
        {benefits.map((benefit) => (
          <span className="tool-live-stat-chip" key={benefit}>
            {benefit}
          </span>
        ))}
      </div>

      <div className="tool-file-seo-wrap">
        <div className="tool-file-seo-layout">
          <div className="tool-live-panel tool-file-seo-panel tool-file-seo-panel-main">
            <div className="tool-utm-step">
              <div className="tool-file-seo-step-heading">
                <span className="eyebrow">1. Upload</span>
                <h2>Start with the image batch</h2>
                <p>Add product shots once, then build a single naming pattern for the full set.</p>
              </div>
              <div
                className={`tool-file-seo-upload ${dragActive ? 'tool-file-seo-upload-active' : ''}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  accept="image/*"
                  className="tool-live-file-input"
                  multiple
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  type="file"
                />
                <strong>
                  {items.length
                    ? `${items.length} image${items.length === 1 ? '' : 's'} ready to rename`
                    : 'Drop product images here'}
                </strong>
                <span>
                  {items.length
                    ? 'Add more files or continue with the naming pattern.'
                    : 'Choose multiple image files or drag them into this area.'}
                </span>
                {items.length ? (
                  <button
                    className="button button-ghost tool-file-seo-upload-action"
                    onClick={(event) => {
                      event.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    type="button"
                  >
                    Add or replace files
                  </button>
                ) : null}
              </div>

              {items.length ? (
                <div className="tool-file-seo-strip" aria-label="Uploaded files">
                  {items.slice(0, 12).map((item) => (
                    <span className="tool-live-stat-chip" key={item.id}>
                      {item.originalName}
                    </span>
                  ))}
                  {items.length > 12 ? (
                    <span className="tool-live-stat-chip">+{items.length - 12} more</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="tool-utm-step">
              <div className="tool-file-seo-step-heading">
                <span className="eyebrow">2. Pattern</span>
                <h2>Choose one clean naming rule</h2>
                <p>
                  Pick a marketplace preset or keep it custom, then fill only the fields you need.
                </p>
              </div>
              <div className="tool-utm-pills" role="radiogroup" aria-label="Rename preset">
                {namingPresets.map((option) => (
                  <button
                    className={`tool-utm-pill ${presetId === option.id ? 'tool-utm-pill-active' : ''}`}
                    key={option.id}
                    onClick={() => setPresetAndDefaults(option.id)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <span className="tool-utm-inline-hint">{preset.hint}</span>

              <div className="tool-file-seo-core-grid">
                <label className="tool-utm-field">
                  <span>Brand</span>
                  <input
                    className="tool-utm-input"
                    onChange={(event) => setBrand(event.target.value)}
                    placeholder="nike"
                    type="text"
                    value={brand}
                  />
                </label>
                <label className="tool-utm-field">
                  <span>Product name</span>
                  <input
                    className="tool-utm-input"
                    onChange={(event) => setProductName(event.target.value)}
                    placeholder="air max"
                    ref={productNameRef}
                    type="text"
                    value={productName}
                  />
                </label>
              </div>
              <small className="tool-file-seo-core-note">
                Product name drives the SEO pattern. If it stays empty, the preview keeps the
                original filenames.
              </small>

              <div className="tool-utm-fields-grid">
                <label className="tool-utm-field tool-utm-field-full">
                  <span>Attributes</span>
                  <input
                    className="tool-utm-input"
                    onChange={(event) => setAttributesInput(event.target.value)}
                    placeholder="black, men, running"
                    type="text"
                    value={attributesInput}
                  />
                  <small>
                    Comma-separated. Good for color, gender, material, view, or size labels.
                  </small>
                </label>
              </div>

              <div
                className={`tool-utm-collapsible ${showOptional ? 'tool-utm-collapsible-open' : ''}`}
              >
                <button
                  aria-expanded={showOptional}
                  className="tool-utm-collapsible-toggle"
                  onClick={() => setShowOptional((current) => !current)}
                  type="button"
                >
                  <span>Optional fields</span>
                  <span>{showOptional ? 'Hide' : 'Show'}</span>
                </button>
                <div className="tool-utm-collapsible-body">
                  <div className="tool-utm-fields-grid">
                    <label className="tool-utm-field">
                      <span>Year</span>
                      <input
                        className="tool-utm-input"
                        inputMode="numeric"
                        onChange={(event) => setYear(event.target.value)}
                        placeholder="2026"
                        type="text"
                        value={year}
                      />
                    </label>
                    <label className="tool-utm-field">
                      <span>Custom suffix</span>
                      <input
                        className="tool-utm-input"
                        onChange={(event) => setCustomSuffix(event.target.value)}
                        placeholder="front-view"
                        type="text"
                        value={customSuffix}
                      />
                    </label>
                  </div>

                  <div className="tool-file-seo-inline-group">
                    <span className="tool-file-seo-inline-label">Keep filenames unique</span>
                    <div className="tool-utm-pills" role="radiogroup" aria-label="Uniqueness mode">
                      <button
                        className={`tool-utm-pill ${uniquenessMode === 'index' ? 'tool-utm-pill-active' : ''}`}
                        onClick={() => setUniquenessMode('index')}
                        type="button"
                      >
                        Add index
                      </button>
                      <button
                        className={`tool-utm-pill ${uniquenessMode === 'original-suffix' ? 'tool-utm-pill-active' : ''}`}
                        onClick={() => setUniquenessMode('original-suffix')}
                        type="button"
                      >
                        Use original suffix
                      </button>
                    </div>
                    <span className="tool-utm-inline-hint">
                      {uniquenessMode === 'index'
                        ? 'Adds `-1`, `-2`, `-3` across the batch.'
                        : 'Keeps a short suffix from each original name when possible.'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="tool-live-panel tool-file-seo-panel tool-file-seo-panel-side">
            <div className="tool-utm-step">
              <div className="tool-file-seo-step-heading">
                <span className="eyebrow">3. Preview</span>
                <h2>Review every renamed file</h2>
                <p>
                  Check the generated pattern, tweak individual rows, and catch issues before
                  export.
                </p>
              </div>
              <div className="tool-file-seo-result tool-file-seo-result-highlight">
                <div>
                  <span className="eyebrow">Sample output</span>
                  <strong>{sampleOutput}</strong>
                </div>
                <div className="tool-file-seo-result-meta">
                  <span className="tool-live-stat-chip">{items.length || 0} files</span>
                  <span className="tool-live-stat-chip">
                    {filledCoreFields ? 'SEO pattern active' : 'Original names fallback'}
                  </span>
                  <span className="tool-live-stat-chip">
                    {uniquenessMode === 'index'
                      ? 'Auto index on duplicates'
                      : 'Original suffix kept'}
                  </span>
                </div>
              </div>

              {!productSlug && items.length ? (
                <div className="tool-batch-stage-note tool-utm-stage-note-warning">
                  <strong>Check</strong>
                  <span>
                    Add a product name to generate SEO-friendly filenames. Until then, the preview
                    falls back to original names.
                  </span>
                </div>
              ) : null}

              <div className="tool-file-seo-table-shell">
                {previewRows.length ? (
                  <table className="tool-file-seo-table">
                    <thead>
                      <tr>
                        <th>Original filename</th>
                        <th>New filename</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row) => (
                        <tr key={row.id}>
                          <td data-label="Original filename">
                            <span className="tool-file-seo-original">{row.originalName}</span>
                          </td>
                          <td data-label="New filename">
                            {editingId === row.id ? (
                              <div className="tool-file-seo-edit-row">
                                <input
                                  autoFocus
                                  className="tool-utm-input"
                                  onBlur={() => commitOverride(row.id)}
                                  onChange={(event) => updateOverride(row.id, event.target.value)}
                                  onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      commitOverride(row.id);
                                    }
                                  }}
                                  type="text"
                                  value={overrides[row.id] ?? row.autoBase}
                                />
                                <span className="tool-file-seo-extension">.{row.extension}</span>
                              </div>
                            ) : (
                              <div className="tool-file-seo-new-name">
                                <span>{row.finalName}</span>
                                {row.edited ? <em>Manual</em> : null}
                              </div>
                            )}
                          </td>
                          <td data-label="Actions">
                            <div className="tool-file-seo-row-actions">
                              <button
                                aria-label={`Edit ${row.originalName}`}
                                className="tool-file-seo-edit-button"
                                onClick={() => {
                                  setEditingId(row.id);
                                  setOverrides((current) => ({
                                    ...current,
                                    [row.id]: current[row.id] ?? row.autoBase,
                                  }));
                                }}
                                type="button"
                              >
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                  <path
                                    d="M4 15.5V20h4.5L19 9.5 14.5 5 4 15.5Z"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.7"
                                  />
                                  <path
                                    d="M12.5 7 17 11.5"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.7"
                                  />
                                </svg>
                              </button>
                              <button
                                aria-label={`Remove ${row.originalName}`}
                                className="tool-file-seo-edit-button"
                                onClick={() => removeItem(row.id)}
                                type="button"
                              >
                                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                  <path
                                    d="M6 7h12M9.5 7V5h5v2M9 10.5v5M15 10.5v5M7.5 7l.7 10.2A2 2 0 0 0 10.2 19h3.6a2 2 0 0 0 2-1.8L16.5 7"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.7"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="tool-file-seo-empty">
                    <strong>No files in the batch yet</strong>
                    <span>Add product images to preview SEO-friendly filenames here.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="tool-utm-step">
              <div className="tool-file-seo-step-heading">
                <span className="eyebrow">4. Download</span>
                <h2>Export or copy the result</h2>
                <p>Download the renamed batch as a ZIP or copy the final names and rename map.</p>
              </div>
              <div className="tool-live-actions tool-file-seo-actions">
                <button
                  className="button tool-file-seo-primary-action"
                  onClick={downloadZip}
                  type="button"
                >
                  {isPreparingZip ? 'Preparing ZIP…' : 'Download all as ZIP'}
                </button>
                <button className="button button-ghost" onClick={copyNames} type="button">
                  Copy names
                </button>
                <button className="button button-ghost" onClick={copyMapping} type="button">
                  Copy mapping
                </button>
                {items.length ? (
                  <button className="button button-ghost" onClick={clearAll} type="button">
                    Clear all
                  </button>
                ) : null}
              </div>

              {actionMessage ? (
                <div
                  className={`tool-batch-stage-note ${
                    actionTone === 'success'
                      ? 'tool-utm-stage-note-success'
                      : actionTone === 'warning'
                        ? 'tool-utm-stage-note-warning'
                        : ''
                  }`}
                >
                  <strong>
                    {actionTone === 'success'
                      ? 'Done'
                      : actionTone === 'warning'
                        ? 'Check'
                        : 'Note'}
                  </strong>
                  <span>{actionMessage}</span>
                </div>
              ) : (
                <div className="tool-batch-stage-note">
                  <strong>Local only</strong>
                  <span>
                    Files stay in the browser. ZIP export renames them locally and does not upload
                    anything.
                  </span>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
